<template>
  <section>
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div class="eyebrow">Obrigações e pagamentos</div>
        <h1 class="mt-2 text-3xl font-extrabold text-[#102a5a]">{{ pageTitle }}</h1>
        <p class="mt-1 text-sm text-slate-500">{{ pageSubtitle }}</p>
      </div>
      <div class="document-page-actions">
        <router-link class="btn-secondary" to="/obrigacoes"><AppIcon name="chevronLeft" class="h-4 w-4" />Voltar ao grid</router-link>
        <router-link v-if="isDetail && form.statusCode === 'DRAFT'" class="btn-primary" :to="`/obrigacoes/${form.id}/editar`"><AppIcon name="pencil" class="h-4 w-4" />Continuar edição</router-link>
        <router-link v-if="isDetail && canReceivePayment" class="btn-primary" :to="`/pagamentos/novo?installmentId=${firstPayableInstallmentId}`"><AppIcon name="money" class="h-4 w-4" />Registrar pagamento</router-link>
      </div>
    </div>

    <section v-if="loading" class="card document-loading"><span class="loading-spinner" />Carregando documento...</section>

    <form v-else class="document-form" @submit.prevent>
      <section v-if="form.id" class="document-summary-strip">
        <div><span>Situação</span><strong><span class="obligation-status" :class="statusClass(form.statusCode)">{{ form.statusName }}</span></strong></div>
        <div><span>Total</span><strong>{{ formatCurrency(form.totalAmount) }}</strong></div>
        <div><span>Pago</span><strong>{{ formatCurrency(form.paidAmount) }}</strong></div>
        <div><span>Saldo</span><strong>{{ formatCurrency(form.outstandingAmount) }}</strong></div>
        <div><span>Próximo vencimento</span><strong>{{ formatDate(form.nextDueDate) }}</strong></div>
        <div><span>Responsável</span><strong>{{ form.updatedBy || form.createdBy }}</strong></div>
      </section>

      <section v-if="!editable && !isDetail" class="document-notice warning">
        Este documento não está mais em rascunho e não pode ser alterado nesta etapa. Os dados são exibidos somente para consulta.
      </section>

      <section class="card overflow-hidden">
        <div class="document-section-header">
          <span class="icon-box"><AppIcon name="file" /></span>
          <div><div class="eyebrow">Cabeçalho</div><h2>Dados do documento</h2></div>
          <span v-if="editable" class="badge-amber">Campos obrigatórios *</span>
        </div>
        <div class="document-fields-grid">
          <label><span class="label">Tipo de documento *</span><select v-model.number="form.documentTypeId" class="input" :disabled="!editable"><option :value="0" disabled>Selecione</option><option v-for="option in options.documentTypes" :key="option.id" :value="option.id" :disabled="!option.isActive && option.id !== form.documentTypeId">{{ optionLabel(option) }}</option></select></label>
          <label><span class="label">Número do documento *</span><input v-model.trim="form.documentNumber" class="input" type="text" maxlength="50" autocomplete="off" :disabled="!editable" placeholder="Ex.: FAT-2026-001"></label>
          <label><span class="label">Data de emissão *</span><input v-model="form.issueDate" class="input" type="date" :disabled="!editable"></label>
          <label><span class="label">Código de origem</span><input v-model.trim="form.sourceCode" class="input" type="text" maxlength="50" autocomplete="off" :disabled="!editable" placeholder="Referência do sistema de origem"></label>
          <label class="field-span-6"><span class="label">Entidade proprietária/credora *</span><select v-model.number="form.ownerId" class="input" :disabled="!editable"><option :value="0" disabled>Selecione</option><option v-for="option in options.entities" :key="option.id" :value="option.id" :disabled="!option.isActive && option.id !== form.ownerId">{{ optionLabel(option) }}</option></select></label>
          <label class="field-span-6"><span class="label">Contraparte/devedora *</span><select v-model.number="form.counterpartyId" class="input" :disabled="!editable"><option :value="0" disabled>Selecione</option><option v-for="option in options.entities" :key="option.id" :value="option.id" :disabled="option.id === form.ownerId || (!option.isActive && option.id !== form.counterpartyId)">{{ optionLabel(option) }}</option></select></label>
          <label class="field-span-12"><span class="label">Descrição</span><textarea v-model.trim="form.description" class="input document-textarea" maxlength="500" rows="3" :disabled="!editable" placeholder="Informações complementares sobre a obrigação" /></label>
        </div>
      </section>

      <section class="card overflow-hidden">
        <div class="document-section-header">
          <span class="icon-box"><AppIcon name="receipt" /></span>
          <div><div class="eyebrow">Composição financeira</div><h2>Itens do documento</h2><p>Os itens explicam como o valor total foi formado.</p></div>
          <button v-if="editable" class="btn-secondary section-add-button" type="button" @click="addItem"><AppIcon name="plus" class="h-4 w-4" />Adicionar item</button>
        </div>
        <div class="document-table-scroll">
          <table class="document-entry-table item-entry-table">
            <thead><tr><th>#</th><th>Natureza *</th><th>Descrição *</th><th>Quantidade *</th><th>Valor unitário *</th><th>Competência</th><th>Total</th><th v-if="editable" aria-label="Ações" /></tr></thead>
            <tbody>
              <tr v-if="form.items.length === 0"><td :colspan="editable ? 8 : 7" class="empty-state">Nenhum item incluído.</td></tr>
              <tr v-for="(item, index) in form.items" :key="item.localId || item.id">
                <td><input v-model.number="item.number" class="input compact-input number-input" type="number" min="1" :disabled="!editable" :aria-label="`Número do item ${index + 1}`"></td>
                <td><select v-model.number="item.typeId" class="input compact-input item-type-select" :disabled="!editable"><option :value="0" disabled>Selecione</option><option v-for="option in options.itemTypes" :key="option.id" :value="option.id" :disabled="!option.isActive && option.id !== item.typeId">{{ itemTypeLabel(option) }}</option></select></td>
                <td><input v-model.trim="item.description" class="input compact-input item-description-input" type="text" maxlength="300" :disabled="!editable" placeholder="Descrição do componente"></td>
                <td><input v-model.number="item.quantity" class="input compact-input numeric-input" type="number" min="0.0001" step="0.0001" :disabled="!editable"></td>
                <td><input v-model.number="item.unitAmount" class="input compact-input numeric-input" type="number" step="0.000001" :disabled="!editable"></td>
                <td><input v-model="item.competenceDate" class="input compact-input date-input" type="date" :disabled="!editable"></td>
                <td class="entry-total">{{ formatCurrency(itemTotal(item)) }}</td>
                <td v-if="editable" class="entry-actions"><button class="row-action delete" type="button" title="Remover item" aria-label="Remover item" @click="removeItem(index)"><AppIcon name="trash" /></button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="document-table-total"><span>Total dos itens</span><strong>{{ formatCurrency(documentTotal) }}</strong></div>
      </section>

      <section class="card overflow-hidden">
        <div class="document-section-header">
          <span class="icon-box"><AppIcon name="calendar" /></span>
          <div><div class="eyebrow">Condições de pagamento</div><h2>Parcelas e vencimentos</h2><p>As parcelas distribuem o total do documento no tempo.</p></div>
          <button v-if="editable" class="btn-secondary section-add-button" type="button" @click="addInstallment"><AppIcon name="plus" class="h-4 w-4" />Adicionar parcela</button>
        </div>
        <div class="document-table-scroll">
          <table class="document-entry-table installment-entry-table">
            <thead><tr><th>Parcela *</th><th>Vencimento *</th><th>Valor original *</th><th v-if="form.id">Valor pago</th><th v-if="form.id">Saldo</th><th v-if="form.id">Situação</th><th v-if="editable || showInstallmentActions" aria-label="Ações" /></tr></thead>
            <tbody>
              <tr v-if="form.installments.length === 0"><td :colspan="installmentColspan" class="empty-state">Nenhuma parcela incluída.</td></tr>
              <tr v-for="(installment, index) in form.installments" :key="installment.localId || installment.id">
                <td><input v-model.number="installment.number" class="input compact-input number-input" type="number" min="1" :disabled="!editable" :aria-label="`Número da parcela ${index + 1}`"></td>
                <td><input v-model="installment.dueDate" class="input compact-input date-input" type="date" :disabled="!editable"></td>
                <td><input v-model.number="installment.originalAmount" class="input compact-input numeric-input" type="number" min="0.01" step="0.01" :disabled="!editable"></td>
                <td v-if="form.id" class="entry-total">{{ formatCurrency(installment.paidAmount) }}</td>
                <td v-if="form.id" class="entry-total">{{ formatCurrency(installment.outstandingAmount) }}</td>
                <td v-if="form.id"><span class="obligation-status" :class="statusClass(installment.statusCode)">{{ installment.statusName }}</span></td>
                <td v-if="editable" class="entry-actions"><button class="row-action delete" type="button" title="Remover parcela" aria-label="Remover parcela" @click="removeInstallment(index)"><AppIcon name="trash" /></button></td>
                <td v-else-if="showInstallmentActions" class="entry-actions"><router-link v-if="installment.outstandingAmount > 0" class="row-action pay" :to="`/pagamentos/novo?installmentId=${installment.id}`" title="Registrar pagamento" aria-label="Registrar pagamento"><AppIcon name="money" /></router-link></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="installment-footer">
          <button v-if="editable && form.installments.length === 1" class="inline-action" type="button" @click="syncSingleInstallment">Usar o total dos itens na parcela única</button>
          <div class="document-table-total"><span>Total das parcelas</span><strong>{{ formatCurrency(installmentsTotal) }}</strong></div>
        </div>
      </section>

      <section v-if="form.id" class="card overflow-hidden">
        <div class="document-section-header">
          <span class="icon-box"><AppIcon name="money" /></span>
          <div><div class="eyebrow">Movimentação financeira</div><h2>Pagamentos e baixas</h2><p>Histórico dos pagamentos relacionados às parcelas desta obrigação.</p></div>
          <router-link v-if="canReceivePayment" class="btn-secondary section-add-button" :to="`/pagamentos/novo?installmentId=${firstPayableInstallmentId}`"><AppIcon name="plus" class="h-4 w-4" />Registrar pagamento</router-link>
        </div>
        <div class="document-table-scroll">
          <table class="document-entry-table payment-history-table">
            <thead><tr><th>Pagamento</th><th>Data</th><th>Método/conta</th><th>Pagadora</th><th>Referência</th><th>Valor</th><th>Baixas neste documento</th><th>Situação</th></tr></thead>
            <tbody>
              <tr v-if="form.payments.length === 0"><td colspan="8" class="empty-state">Nenhum pagamento relacionado a esta obrigação.</td></tr>
              <tr v-for="payment in form.payments" v-else :key="payment.id">
                <td><router-link class="document-link" :to="`/pagamentos/${payment.id}`"><strong>#{{ payment.id }}</strong></router-link><small>{{ payment.description || `Registrado por ${payment.createdBy}` }}</small></td>
                <td>{{ formatDateTime(payment.paidAt || payment.createdAt) }}</td>
                <td>{{ payment.paymentMethodName }}<small>{{ payment.bankAccountName || 'Conta não informada' }}</small></td>
                <td>{{ payment.payerName }}<small>Recebedora: {{ payment.receiverName }}</small></td>
                <td>{{ payment.externalReference || '—' }}</td>
                <td class="entry-total">{{ formatCurrency(payment.amount) }}</td>
                <td><span v-for="allocation in payment.allocations" :key="allocation.allocationId" class="allocation-line">Parcela {{ allocation.installmentNumber }}: <strong>{{ formatCurrency(allocation.netAmount) }}</strong><small v-if="allocation.reversedAmount > 0">Alocado {{ formatCurrency(allocation.allocatedAmount) }} · revertido {{ formatCurrency(allocation.reversedAmount) }}</small></span></td>
                <td><span class="obligation-status" :class="statusClass(payment.statusCode)">{{ payment.statusName }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="card overflow-hidden">
        <div class="document-section-header">
          <span class="icon-box"><AppIcon name="paperclip" /></span>
          <div><div class="eyebrow">Documentos comprobatórios</div><h2>Anexos opcionais</h2><p>Notas fiscais, contratos, boletos e outros documentos relacionados à obrigação.</p></div>
          <span class="badge-blue">Opcional</span>
        </div>

        <div v-if="!form.id" class="attachment-save-first">
          Salve o documento como rascunho ou em aberto para habilitar a inclusão de anexos.
        </div>

        <template v-else>
          <div v-if="canWriteObligations" class="attachment-upload-panel">
            <label class="attachment-file-field">
              <span class="label">Arquivo</span>
              <input ref="attachmentInput" class="input attachment-file-input" type="file" :accept="attachmentAccept" :disabled="uploadingAttachment" @change="selectAttachment">
              <small>Permitidos: {{ allowedExtensionsLabel }}. Limite: {{ formatFileSize(attachmentConfiguration.maxFileSizeBytes) }}.</small>
            </label>
            <label class="attachment-description-field">
              <span class="label">Descrição</span>
              <input v-model.trim="attachmentDescription" class="input" type="text" maxlength="300" :disabled="uploadingAttachment" placeholder="Ex.: Nota fiscal referente ao serviço">
            </label>
            <button class="btn-primary attachment-upload-button" type="button" :disabled="uploadingAttachment || !attachmentFile" @click="uploadAttachment">
              <AppIcon name="upload" class="h-4 w-4" />{{ uploadingAttachment ? 'Enviando...' : 'Enviar anexo' }}
            </button>
          </div>

          <div class="document-table-scroll attachment-list-wrap">
            <table class="document-entry-table attachment-table">
              <thead><tr><th>Arquivo</th><th>Descrição</th><th>Tipo</th><th>Tamanho</th><th>Enviado por</th><th>Data</th><th>Situação</th><th aria-label="Ações" /></tr></thead>
              <tbody>
                <tr v-if="loadingAttachments"><td colspan="8" class="empty-state">Carregando anexos...</td></tr>
                <tr v-else-if="attachments.length === 0"><td colspan="8" class="empty-state">Nenhum anexo incluído. O documento permanece válido sem anexos.</td></tr>
                <tr v-for="attachment in attachments" v-else :key="attachment.attachmentId" :class="{ 'inactive-attachment': !attachment.isActive }">
                  <td><strong class="attachment-name">{{ attachment.fileName }}</strong><small v-if="attachment.checksumSha256" class="attachment-checksum" :title="attachment.checksumSha256">SHA-256: {{ attachment.checksumSha256.slice(0, 12) }}…</small></td>
                  <td>{{ attachment.description || '—' }}</td>
                  <td><span class="code-chip">{{ fileExtension(attachment.fileName) }}</span></td>
                  <td>{{ formatFileSize(attachment.fileSize) }}</td>
                  <td>{{ attachment.uploadedBy }}</td>
                  <td>{{ formatDateTime(attachment.createdAt) }}</td>
                  <td><span :class="attachment.isActive ? 'badge-green' : 'badge-red'">{{ attachment.isActive ? 'Ativo' : 'Inativo' }}</span></td>
                  <td class="entry-actions"><span class="row-actions">
                    <button class="row-action view" type="button" title="Baixar anexo" aria-label="Baixar anexo" @click="downloadAttachment(attachment)"><AppIcon name="download" /></button>
                    <button v-if="canWriteObligations && attachment.isActive" class="row-action delete" type="button" title="Inativar anexo" aria-label="Inativar anexo" @click="inactivateAttachment(attachment)"><AppIcon name="close" /></button>
                  </span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </section>

      <section v-if="form.id" class="card overflow-hidden">
        <div class="document-section-header"><span class="icon-box"><AppIcon name="transactions" /></span><div><div class="eyebrow">Rastreabilidade</div><h2>Histórico básico</h2><p>Criação, alterações, anexos, pagamentos e reversões relacionados à obrigação.</p></div></div>
        <div class="history-timeline"><article v-for="event in form.history" :key="`${event.sourceType}-${event.sourceId}-${event.occurredAt}`" class="history-event"><span class="history-marker" /><div><div class="history-event-header"><strong>{{ event.title }}</strong><time>{{ formatDateTime(event.occurredAt) }}</time></div><p v-if="event.description">{{ event.description }}</p><small>Responsável: {{ event.actor }}</small></div></article><div v-if="!form.history || form.history.length === 0" class="empty-state">Nenhum evento encontrado.</div></div>
      </section>

      <section v-if="form.id" class="detail-audit-strip">
        <div><span>Criado em</span><strong>{{ formatDateTime(form.createdAt) }}</strong><small>{{ form.createdBy }}</small></div>
        <div><span>Última atualização</span><strong>{{ formatDateTime(form.updatedAt) }}</strong><small>{{ form.updatedBy || 'Sem alteração posterior' }}</small></div>
      </section>

      <section class="document-balance-card" :class="hasBalancedTotals ? 'balanced' : 'unbalanced'">
        <div><span>Itens</span><strong>{{ formatCurrency(documentTotal) }}</strong></div>
        <div><span>Parcelas</span><strong>{{ formatCurrency(installmentsTotal) }}</strong></div>
        <div><span>Diferença</span><strong>{{ formatCurrency(totalDifference) }}</strong></div>
        <p>{{ hasBalancedTotals ? 'Os valores estão consistentes.' : 'A soma das parcelas deve ser exatamente igual ao total dos itens.' }}</p>
      </section>

      <div v-if="editable" class="document-form-actions">
        <router-link class="btn-secondary" to="/obrigacoes">Cancelar</router-link>
        <button class="btn-secondary" type="button" :disabled="saving" @click="save('DRAFT')"><AppIcon name="save" class="h-4 w-4" />{{ savingMode === 'DRAFT' ? 'Salvando...' : 'Salvar rascunho' }}</button>
        <button class="btn-primary" type="button" :disabled="saving" @click="save('OPEN')"><AppIcon name="check" class="h-4 w-4" />{{ savingMode === 'OPEN' ? 'Ativando...' : 'Salvar e ativar' }}</button>
      </div>
    </form>
  </section>
</template>

<script>
import Swal from 'sweetalert2'
import AppIcon from '../components/AppIcon.vue'
import service from '../services/financialDocumentService'
import accessService from '../services/financialAccessService'

const today = () => {
  const value = new Date()
  return new Date(value.getTime() - (value.getTimezoneOffset() * 60000)).toISOString().slice(0, 10)
}
const dateOnly = value => value ? String(value).slice(0, 10) : ''

export default {
  name: 'FinancialDocumentFormView',
  components: { AppIcon },
  data: () => ({
    form: {
      id: 0,
      documentTypeId: 0,
      ownerId: 0,
      counterpartyId: 0,
      sourceCode: '',
      documentNumber: '',
      issueDate: today(),
      totalAmount: 0,
      description: '',
      statusCode: '',
      statusName: '',
      createdAt: null,
      createdBy: '',
      updatedAt: null,
      updatedBy: '',
      paidAmount: 0,
      outstandingAmount: 0,
      nextDueDate: null,
      items: [],
      installments: [],
      payments: [],
      history: []
    },
    options: { documentTypes: [], entities: [], statuses: [], itemTypes: [] },
    loading: true,
    saving: false,
    savingMode: '',
    localSequence: 0,
    attachments: [],
    attachmentConfiguration: { maxFileSizeBytes: 0, allowedExtensions: [] },
    attachmentFile: null,
    attachmentDescription: '',
    loadingAttachments: false,
    uploadingAttachment: false,
    canWriteObligations: false,
    canWritePayments: false
  }),
  computed: {
    routeId () { return Number(this.$route.params.id || 0) },
    isDetail () { return this.$route.name === 'FinancialDocumentDetail' },
    editable () { return this.routeId === 0 || (this.$route.name === 'FinancialDocumentEdit' && this.form.statusCode === 'DRAFT') },
    pageTitle () {
      if (!this.routeId) return 'Incluir documento'
      return this.isDetail ? `Documento ${this.form.documentNumber || ''}` : `Editar documento ${this.form.documentNumber || ''}`
    },
    pageSubtitle () {
      if (!this.routeId) return 'Registre a obrigação completa, sua composição e os respectivos vencimentos.'
      return this.isDetail ? 'Consulte a composição financeira e os vencimentos da obrigação.' : 'Rascunhos podem ser ajustados antes da ativação.'
    },
    documentTotal () { return this.roundMoney(this.form.items.reduce((sum, item) => sum + this.itemTotal(item), 0)) },
    installmentsTotal () { return this.roundMoney(this.form.installments.reduce((sum, installment) => sum + this.roundMoney(installment.originalAmount), 0)) },
    totalDifference () { return this.roundMoney(this.installmentsTotal - this.documentTotal) },
    hasBalancedTotals () { return this.documentTotal > 0 && Math.abs(this.totalDifference) < 0.005 },
    showInstallmentActions () { return Boolean(this.form.id && !this.editable && this.canReceivePayment) },
    firstPayableInstallmentId () { return this.form.installments.find(item => Number(item.outstandingAmount) > 0)?.id || 0 },
    canReceivePayment () { return this.canWritePayments && ['OPEN', 'PARTIALLY_PAID'].includes(this.form.statusCode) && this.firstPayableInstallmentId > 0 },
    installmentColspan () { return (this.form.id ? 6 : 3) + ((this.editable || this.showInstallmentActions) ? 1 : 0) },
    attachmentAccept () { return this.attachmentConfiguration.allowedExtensions.join(',') },
    allowedExtensionsLabel () { return this.attachmentConfiguration.allowedExtensions.map(value => value.replace('.', '').toUpperCase()).join(', ') || 'conforme configuração' }
  },
  watch: {
    '$route.fullPath': 'initialize'
  },
  created () {
    this.initialize()
  },
  methods: {
    async initialize () {
      this.loading = true
      try {
        const [options, attachmentConfiguration, canWriteObligations, canWritePayments] = await Promise.all([
          service.getOptions(),
          service.getAttachmentConfiguration(),
          accessService.has('OBLIGATIONS', 2),
          accessService.has('PAYMENTS', 2)
        ])
        this.options = options
        this.attachmentConfiguration = attachmentConfiguration
        this.canWriteObligations = canWriteObligations
        this.canWritePayments = canWritePayments
        if (this.routeId) {
          await this.loadDocument()
          await this.loadAttachments()
        } else this.createNewDocument()
      } catch (error) {
        await this.showError(error)
      } finally {
        this.loading = false
      }
    },
    createNewDocument () {
      this.form = {
        id: 0,
        documentTypeId: 0,
        ownerId: 0,
        counterpartyId: 0,
        sourceCode: '',
        documentNumber: '',
        issueDate: today(),
        totalAmount: 0,
        description: '',
        statusCode: '',
        statusName: '',
        createdAt: null,
        createdBy: '',
        updatedAt: null,
        updatedBy: '',
        paidAmount: 0,
        outstandingAmount: 0,
        nextDueDate: null,
        items: [],
        installments: [],
        payments: [],
        history: []
      }
      this.addItem()
      this.addInstallment()
    },
    async loadDocument () {
      const result = await service.getById(this.routeId)
      this.form = {
        ...result,
        issueDate: dateOnly(result.issueDate),
        sourceCode: result.sourceCode || '',
        description: result.description || '',
        items: result.items.map(item => ({ ...item, localId: `item-${++this.localSequence}`, competenceDate: dateOnly(item.competenceDate) })),
        installments: result.installments.map(installment => ({ ...installment, localId: `installment-${++this.localSequence}`, dueDate: dateOnly(installment.dueDate) }))
      }
    },
    async loadAttachments () {
      this.loadingAttachments = true
      try {
        this.attachments = await service.getAttachments(this.routeId)
      } finally {
        this.loadingAttachments = false
      }
    },
    selectAttachment (event) {
      this.attachmentFile = event.target.files?.[0] || null
    },
    async uploadAttachment () {
      if (!this.attachmentFile) return
      const extension = `.${this.attachmentFile.name.split('.').pop().toLowerCase()}`
      if (!this.attachmentConfiguration.allowedExtensions.map(value => value.toLowerCase()).includes(extension)) {
        await Swal.fire({ title: 'Atenção', text: `O tipo de arquivo ${extension} não é permitido.`, icon: 'warning', confirmButtonColor: '#e77900' })
        return
      }
      if (this.attachmentFile.size > this.attachmentConfiguration.maxFileSizeBytes) {
        await Swal.fire({ title: 'Atenção', text: `O arquivo ultrapassa o limite de ${this.formatFileSize(this.attachmentConfiguration.maxFileSizeBytes)}.`, icon: 'warning', confirmButtonColor: '#e77900' })
        return
      }

      this.uploadingAttachment = true
      try {
        await service.addAttachment(this.form.id, this.attachmentFile, this.attachmentDescription)
        this.attachmentFile = null
        this.attachmentDescription = ''
        if (this.$refs.attachmentInput) this.$refs.attachmentInput.value = ''
        await this.loadAttachments()
        await Swal.fire({ icon: 'success', title: 'Anexo incluído com sucesso.', timer: 1600, showConfirmButton: false })
      } catch (error) {
        await this.showError(error)
      } finally {
        this.uploadingAttachment = false
      }
    },
    async downloadAttachment (attachment) {
      try {
        const blob = await service.downloadAttachment(this.form.id, attachment.attachmentId)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = attachment.fileName
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (error) {
        await this.showError(error)
      }
    },
    async inactivateAttachment (attachment) {
      const confirmation = await Swal.fire({
        icon: 'warning',
        title: `Inativar ${attachment.fileName}?`,
        text: 'O arquivo será preservado para histórico e continuará disponível para download.',
        showCancelButton: true,
        confirmButtonText: 'Inativar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#e77900',
        reverseButtons: true
      })
      if (!confirmation.isConfirmed) return
      try {
        await service.inactivateAttachment(this.form.id, attachment.attachmentId)
        await this.loadAttachments()
      } catch (error) {
        await this.showError(error)
      }
    },
    addItem () {
      const defaultType = this.options.itemTypes.find(option => option.code === 'PRINCIPAL' && option.isActive) || this.options.itemTypes.find(option => option.isActive)
      this.form.items.push({
        id: 0,
        localId: `item-${++this.localSequence}`,
        typeId: defaultType?.id || 0,
        number: this.nextNumber(this.form.items),
        description: '',
        quantity: 1,
        unitAmount: 0,
        competenceDate: this.form.issueDate,
        sourceCode: ''
      })
    },
    removeItem (index) { this.form.items.splice(index, 1) },
    addInstallment () {
      this.form.installments.push({
        id: 0,
        localId: `installment-${++this.localSequence}`,
        number: this.nextNumber(this.form.installments),
        dueDate: this.form.issueDate || today(),
        originalAmount: 0,
        paidAmount: 0,
        outstandingAmount: 0,
        statusCode: '',
        statusName: ''
      })
    },
    removeInstallment (index) { this.form.installments.splice(index, 1) },
    nextNumber (entries) { return entries.length === 0 ? 1 : Math.max(...entries.map(entry => Number(entry.number || 0))) + 1 },
    syncSingleInstallment () { this.form.installments[0].originalAmount = this.documentTotal },
    itemTotal (item) { return this.roundMoney(this.roundValue(item.quantity, 4) * this.roundValue(item.unitAmount, 6)) },
    roundMoney (value) { return this.roundValue(value, 2) },
    roundValue (value, decimals) {
      const factor = 10 ** decimals
      const number = Number(value || 0)
      return Math.sign(number) * Math.round((Math.abs(number) + Number.EPSILON) * factor) / factor
    },
    itemTypeLabel (option) {
      const signal = option.amountBehavior === 'P' ? '+' : (option.amountBehavior === 'N' ? '−' : '±')
      return `${this.optionLabel(option)} (${signal})`
    },
    optionLabel (option) { return option.isActive ? option.name : `${option.name} (inativo)` },
    async save (saveMode) {
      const validationMessage = this.validate()
      if (validationMessage) {
        await Swal.fire({ title: 'Atenção', text: validationMessage, icon: 'warning', confirmButtonColor: '#e77900' })
        return
      }

      if (saveMode === 'OPEN') {
        const confirmation = await Swal.fire({
          icon: 'question',
          title: 'Ativar esta obrigação?',
          text: 'Após a ativação, itens e parcelas ficarão somente para consulta nesta entrega.',
          showCancelButton: true,
          confirmButtonText: 'Salvar e ativar',
          cancelButtonText: 'Revisar',
          confirmButtonColor: '#e77900',
          reverseButtons: true
        })
        if (!confirmation.isConfirmed) return
      }

      this.saving = true
      this.savingMode = saveMode
      try {
        const payload = {
          id: this.form.id,
          documentTypeId: this.form.documentTypeId,
          ownerId: this.form.ownerId,
          counterpartyId: this.form.counterpartyId,
          sourceCode: this.form.sourceCode || null,
          documentNumber: this.form.documentNumber,
          issueDate: this.form.issueDate,
          description: this.form.description || null,
          saveMode,
          items: this.form.items.map(item => ({
            typeId: item.typeId,
            number: item.number,
            description: item.description,
            quantity: Number(item.quantity),
            unitAmount: Number(item.unitAmount),
            competenceDate: item.competenceDate || null,
            sourceCode: item.sourceCode || null
          })),
          installments: this.form.installments.map(installment => ({
            number: installment.number,
            dueDate: installment.dueDate,
            originalAmount: Number(installment.originalAmount)
          }))
        }

        let id = this.form.id
        if (id) await service.update(payload)
        else id = await service.add(payload)

        await Swal.fire({ icon: 'success', title: saveMode === 'OPEN' ? 'Obrigação ativada com sucesso.' : 'Rascunho salvo com sucesso.', timer: 1800, showConfirmButton: false })
        await this.$router.push(`/obrigacoes/${id}`)
      } catch (error) {
        await this.showError(error)
      } finally {
        this.saving = false
        this.savingMode = ''
      }
    },
    validate () {
      if (!this.form.documentTypeId || !this.form.ownerId || !this.form.counterpartyId || !this.form.documentNumber || !this.form.issueDate) return 'Informe todos os campos obrigatórios do documento.'
      if (this.form.ownerId === this.form.counterpartyId) return 'A entidade proprietária e a contraparte devem ser diferentes.'
      if (this.form.items.length === 0) return 'Inclua ao menos um item no documento.'
      if (this.form.installments.length === 0) return 'Inclua ao menos uma parcela no documento.'
      if (new Set(this.form.items.map(item => Number(item.number))).size !== this.form.items.length) return 'A numeração dos itens não pode se repetir.'
      if (new Set(this.form.installments.map(installment => Number(installment.number))).size !== this.form.installments.length) return 'A numeração das parcelas não pode se repetir.'
      if (this.form.items.some(item => !item.typeId || Number(item.number) <= 0 || !item.description || Number(item.quantity) <= 0 || Number(item.unitAmount) === 0)) return 'Preencha corretamente todos os itens.'
      if (this.form.items.some(item => {
        const type = this.options.itemTypes.find(option => option.id === item.typeId)
        const total = this.itemTotal(item)
        return !type || !type.isActive || (type.amountBehavior === 'P' && total <= 0) || (type.amountBehavior === 'N' && total >= 0) || total === 0
      })) return 'O sinal do valor de um item não é compatível com sua natureza ou a natureza está inativa.'
      if (this.form.installments.some(installment => Number(installment.number) <= 0 || !installment.dueDate || Number(installment.originalAmount) <= 0)) return 'Preencha corretamente todas as parcelas.'
      if (!this.hasBalancedTotals) return 'A soma das parcelas deve ser exatamente igual ao total dos itens.'
      return ''
    },
    formatCurrency (value) { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0)) },
    formatDate (value) { return value ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${String(value).slice(0, 10)}T12:00:00`)) : '—' },
    formatDateTime (value) { return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)).replace(',', '') : '—' },
    formatFileSize (value) {
      const bytes = Number(value || 0)
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    fileExtension (fileName) { return fileName.includes('.') ? fileName.split('.').pop().toUpperCase() : 'ARQUIVO' },
    statusClass (code) { return `status-${String(code || '').toLowerCase().replaceAll('_', '-')}` },
    async showError (error) {
      const validationErrors = error?.response?.data?.errors
      const firstValidation = validationErrors ? Object.values(validationErrors).flat()[0] : null
      const message = firstValidation || error?.response?.data?.title || error?.response?.data?.detail || 'Não foi possível concluir a operação.'
      await Swal.fire({ title: 'Atenção', text: message, icon: 'error', confirmButtonColor: '#e77900' })
    }
  }
}
</script>
